import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { getConfigEditorBridgeState, subscribeConfigEditorBridge } from 'config-editor-common';
import { AiAssistantClient } from '../api/AiAssistantClient';
import { useAiDriver, type DriverStatus } from '../driver/useAiDriver';
import type { PageContext, PlanStep } from '../types';

export type ChatMessageKind = 'summary' | 'clarify' | 'error' | 'plain';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  kind: ChatMessageKind;
  /** Clickable follow-up choices (clarify disambiguation); clicking re-asks with the chosen label. */
  options?: string[];
  /** Epoch ms the message was added — rendered as a time label (also included when copying). */
  ts?: number;
}

export interface AiAssistantContextValue {
  messages: ChatMessage[];
  sending: boolean;
  /** Resolves true when the turn was handled; false on a failure the composer should keep the text for. */
  sendMessage: (text: string) => Promise<boolean>;
  clearChat: () => void;
  // driver passthrough (for the widget's step recap + the coach-mark overlay)
  driverStatus: DriverStatus;
  activeStepIndex: number;
  totalSteps: number;
  currentStep: PlanStep | null;
  currentAnchor: string | null;
  driverError: string | null;
  next: () => void;
  cancelPlan: () => void;
}

const AiAssistantContext = createContext<AiAssistantContextValue | undefined>(undefined);

export function useAiAssistant(): AiAssistantContextValue {
  const ctx = useContext(AiAssistantContext);
  if (!ctx) {
    throw new Error('useAiAssistant must be used within an <AiAssistantContextProvider>');
  }
  return ctx;
}

// Chat transcript is persisted in the browser so a page reload doesn't wipe the conversation.
// Bump the version suffix if the ChatMessage shape changes incompatibly.
const TRANSCRIPT_KEY = 'ai-assistant.transcript.v1';
const MAX_PERSISTED = 50;

// Stable per-conversation id sent with each plan request so the backend can scope server-side memory
// (AgentCore) to this session. Persisted next to the transcript; regenerated on "new conversation".
const SESSION_KEY = 'ai-assistant.sessionId.v1';

function newSessionId(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to the non-crypto fallback (e.g. insecure context)
  }
  return `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Load the persisted session id or mint + persist a new one. */
function loadOrCreateSessionId(): string {
  if (typeof window === 'undefined') return newSessionId();
  try {
    const existing = window.localStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id = newSessionId();
    window.localStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return newSessionId();
  }
}

function isChatMessage(m: any): m is ChatMessage {
  return (
    !!m &&
    typeof m.id === 'string' &&
    (m.role === 'user' || m.role === 'assistant') &&
    typeof m.text === 'string' &&
    typeof m.kind === 'string' &&
    // options must be undefined or an array of STRINGS — the widget calls .trim() on each when a chip
    // is clicked, so a non-string (from tampered/legacy storage) would throw at click time.
    (m.options === undefined ||
      (Array.isArray(m.options) && m.options.every((o: unknown) => typeof o === 'string'))) &&
    (m.ts === undefined || typeof m.ts === 'number')
  );
}

/** Restore the last transcript from localStorage (validated + capped). Empty on any failure/SSR. */
function loadTranscript(): ChatMessage[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(TRANSCRIPT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isChatMessage).slice(-MAX_PERSISTED);
  } catch {
    return [];
  }
}

/** Largest numeric suffix among restored m<N> ids, so freshly-minted ids never collide. */
function maxMessageId(messages: ChatMessage[]): number {
  let max = 0;
  for (const m of messages) {
    const match = /^m(\d+)$/.exec(m.id);
    if (match) max = Math.max(max, Number(match[1]));
  }
  return max;
}

export const AiAssistantContextProvider = ({ children }: { children: ReactNode }) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const location = useLocation();
  const client = useMemo(
    () => new AiAssistantClient({ discoveryApi, fetchApi }),
    [discoveryApi, fetchApi],
  );

  const driver = useAiDriver();

  const [messages, setMessages] = useState<ChatMessage[]>(loadTranscript);
  const [sending, setSending] = useState(false);
  // Seed the id counter from the restored transcript exactly once (lazy ref init) so new m<N> ids
  // continue past the highest restored id instead of colliding with it.
  const idRef = useRef(-1);
  if (idRef.current < 0) idRef.current = maxMessageId(messages);
  // Mirror of the transcript for building conversation history at send time without a stale closure.
  const messagesRef = useRef<ChatMessage[]>(messages);
  messagesRef.current = messages;

  // Current conversation's session id (stable across reloads; reset by clearChat).
  const sessionIdRef = useRef('');
  if (!sessionIdRef.current) sessionIdRef.current = loadOrCreateSessionId();

  // Bumped whenever the conversation is reset (clearChat). An in-flight send captures the value at
  // dispatch time; if it changes before the response resolves, the reply belongs to a conversation the
  // user has since cleared — so it is dropped (not shown, not persisted, no UI drive) instead of
  // landing in the fresh conversation.
  const sendGenerationRef = useRef(0);

  // Persist the transcript on every change (capped). Removing on empty keeps storage clean so a
  // reload after "new conversation" starts fresh. Wrapped for quota / private-mode failures.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (messages.length === 0) {
        window.localStorage.removeItem(TRANSCRIPT_KEY);
      } else {
        window.localStorage.setItem(TRANSCRIPT_KEY, JSON.stringify(messages.slice(-MAX_PERSISTED)));
      }
    } catch {
      // ignore: storage may be full or unavailable (private mode)
    }
  }, [messages]);

  // Mirror the config-editor's current selection so we can tell the planner what the user is looking at.
  const [selection, setSelection] = useState(() => getConfigEditorBridgeState().selection);
  useEffect(() => {
    setSelection(getConfigEditorBridgeState().selection);
    return subscribeConfigEditorBridge(() => {
      setSelection(getConfigEditorBridgeState().selection);
    });
  }, []);

  const pushMessage = useCallback(
    (role: ChatMessage['role'], text: string, kind: ChatMessageKind, options?: string[]) => {
      idRef.current += 1;
      const id = `m${idRef.current}`;
      setMessages(prev => [...prev, { id, role, text, kind, options, ts: Date.now() }]);
    },
    [],
  );

  const currentContext = useCallback((): PageContext => {
    // Include the currently-open config's lambda names so the planner can target them without a
    // get_config round-trip (latency win when the user is already on the config-editor page).
    const { edit } = getConfigEditorBridgeState();
    const lambdas = edit?.ready ? edit.lambdaNames : undefined;
    return {
      type: selection?.type,
      component: selection?.component,
      item: selection?.item,
      branch: selection?.branch,
      lambdas: lambdas && lambdas.length > 0 ? lambdas : undefined,
      route: location.pathname,
    };
  }, [selection, location.pathname]);

  const sendMessage = useCallback(
    async (raw: string): Promise<boolean> => {
      const text = raw.trim();
      if (!text || sending) return true;
      // Snapshot the transcript BEFORE adding the current turn — this is the conversation context the
      // planner needs so a follow-up ("FQCN?", "the lambda I asked") resolves against what was just said.
      // Drop error bubbles (no context value); the backend windows this to the last couple of turns.
      const history = messagesRef.current
        .filter(m => m.kind !== 'error')
        .slice(-12)
        .map(m => ({ role: m.role, content: m.text }));
      // Snapshot the conversation generation so a reply that resolves after a "New conversation" is dropped.
      const gen = sendGenerationRef.current;
      pushMessage('user', text, 'plain');
      setSending(true);
      try {
        const result = await client.plan({
          message: text,
          context: currentContext(),
          sessionId: sessionIdRef.current,
          history,
        });

        // The conversation was cleared while this was in flight — discard the stale reply entirely.
        // Treat as handled (don't restore the composer into a new conversation).
        if (sendGenerationRef.current !== gen) return true;

        switch (result.status) {
          case 'ok': {
            const plan = result.plan;
            // Both edit and create plans drive the real UI (edit → config-editor; create → scaffolder)
            // and stop at the terminal step (Save / Create) for the human.
            if (plan.steps && plan.steps.length > 0) {
              pushMessage('assistant', plan.summary, 'summary');
              driver.run(plan);
            } else {
              // Answer / clarify / summary-only plan: nothing to drive. An answer (a question the
              // assistant looked up) renders as a plain reply; a clarify keeps its question styling.
              // Attach any pickable options (candidate matches, one-click follow-ups) as chips.
              pushMessage(
                'assistant',
                plan.answer ?? plan.clarify ?? plan.summary,
                plan.clarify && !plan.answer ? 'clarify' : 'plain',
                plan.answer || plan.clarify ? plan.options : undefined,
              );
            }
            return true;
          }
          case 'invalid':
            // The model failed to produce a valid plan (often an under-specified request). Don't dump
            // internal schema errors at the user — ask for the missing detail instead. Raw errors go
            // to the console for debugging. Return false so the composer keeps the user's text to tweak.
            // eslint-disable-next-line no-console
            console.debug('ai-assistant: invalid plan', result.errors);
            pushMessage(
              'assistant',
              'I couldn\'t work out a plan for that one. Try naming the lambda or API — e.g. "set the timeout on auth_token_authorizer to 30 seconds" or "what\'s the memory of awb_upsert_org?"',
              'clarify',
            );
            return false;
          case 'disabled':
            pushMessage(
              'assistant',
              'The copilot is turned off for this environment. Ask your platform team to enable it.',
              'error',
            );
            return false;
          case 'error':
          default:
            // eslint-disable-next-line no-console
            console.debug('ai-assistant: request failed', result);
            pushMessage(
              'assistant',
              "I couldn't reach the assistant — check your connection and try again in a moment.",
              'error',
            );
            return false;
        }
      } catch (e: any) {
        // Same generation guard as the success path: a failure for a cleared conversation is dropped.
        if (sendGenerationRef.current !== gen) return true;
        // eslint-disable-next-line no-console
        console.debug('ai-assistant: send threw', e);
        pushMessage(
          'assistant',
          "I couldn't reach the assistant — check your connection and try again in a moment.",
          'error',
        );
        return false;
      } finally {
        setSending(false);
      }
    },
    [client, currentContext, driver, pushMessage, sending],
  );

  const clearChat = useCallback(() => {
    // Invalidate any in-flight send so its reply can't land in the new conversation.
    sendGenerationRef.current += 1;
    driver.cancel();
    setMessages([]);
    // Start a fresh server-side memory session so the next conversation doesn't inherit this one.
    const id = newSessionId();
    sessionIdRef.current = id;
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(SESSION_KEY, id);
      } catch {
        // ignore storage failures (private mode / quota)
      }
    }
  }, [driver]);

  const value = useMemo<AiAssistantContextValue>(
    () => ({
      messages,
      sending,
      sendMessage,
      clearChat,
      driverStatus: driver.status,
      activeStepIndex: driver.activeStepIndex,
      totalSteps: driver.totalSteps,
      currentStep: driver.currentStep,
      currentAnchor: driver.currentAnchor,
      driverError: driver.error,
      next: driver.next,
      cancelPlan: driver.cancel,
    }),
    [messages, sending, sendMessage, clearChat, driver],
  );

  return <AiAssistantContext.Provider value={value}>{children}</AiAssistantContext.Provider>;
};
